import React, { useCallback, useState } from 'react';
import { StarRating } from './StarRating';
import { ModalComponent } from '../../../components/Modal/ModalComponent';
import { CHATBOT_CONFIG } from '../config';
import { ChatbotLocalizedLabels, FeedbackQuestion } from '../types';

interface FeedbackSurveyProps {
  questions: FeedbackQuestion[];
  feedbackSurveyID: string;
  isSubmitting: boolean;
  labels: ChatbotLocalizedLabels;
  isOpen: boolean;
  onSubmit: (scores: Record<number, number>, comments: string) => Promise<string | null>;
  onSkip: () => Promise<void>;
}

export const FeedbackSurvey: React.FC<FeedbackSurveyProps> = ({
  questions,
  isSubmitting,
  labels,
  isOpen,
  onSubmit,
  onSkip,
}) => {
  const [scores, setScores] = useState<Record<number, number>>({});
  const [comments, setComments] = useState('');
  const [submitError, setSubmitError] = useState('');

  const hasAtLeastOneRating = Object.keys(scores).length > 0;
  const feedbackMaxLength = CHATBOT_CONFIG.feedbackMaxLength;
  const maxCharsValidationMessage = labels.maxCharsHintTemplate.replace('{maxChar}', `${feedbackMaxLength}`)

  const handleScoreChange = useCallback((questionId: number, score: number) => {
    setScores(prev => ({ ...prev, [questionId]: score }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!hasAtLeastOneRating) return;

    if (comments.length > feedbackMaxLength) {
      setSubmitError(maxCharsValidationMessage);
      return;
    }

    setSubmitError('');
    const errorMessage = await onSubmit(scores, comments.trim());
    if (errorMessage) {
      setSubmitError(errorMessage);
    }
  }, [comments, feedbackMaxLength, hasAtLeastOneRating, maxCharsValidationMessage, onSubmit, scores]);

  const sortedQuestions = [...questions].sort((a, b) => a.displayOrder - b.displayOrder);

  const footerChild = (
    <>
      <button
        type="button"
        className="chatbot-btn chatbot-btn-secondary btn-cancel"
        onClick={onSkip}
        disabled={isSubmitting}
      >
        {labels.surveySkip}
      </button>
      <button
        type="button"
        className="chatbot-btn chatbot-btn-primary"
        onClick={() => {
          void handleSubmit();
        }}
        disabled={!hasAtLeastOneRating || isSubmitting}
      >
        {labels.surveySubmit}
      </button>
    </>
  );

  const modalObject = {
    showModal: isOpen,
    dialogClass: 'chatbot-exit-modal chatbot-survey-dialog',
    head: {
      modalTitle: labels.surveyTitle,
    },
    body: {},
    footer: {
      footerChild,
    },
    clickHandler: onSkip,
    showHeaderCloseBtn: true,
    css: {
      modalTitle: 'modal-title chatbot-survey-title',
      modalFooter: 'chatbot-survey-footer',
    },
  };

  return (
    <ModalComponent modalObject={modalObject}>
      <p className="chatbot-survey-disclaimer">
        {labels.surveyDisclaimer}
      </p>

      {sortedQuestions.map((question) => (
        <div key={question.feedbackQuestionID} className="chatbot-survey-question">
          <p
            id={`chatbot-survey-question-${question.feedbackQuestionID}`}
            className="chatbot-survey-question-text"
          >
            {question.questionText}
          </p>
          <StarRating
            questionId={question.feedbackQuestionID}
            questionLabelId={`chatbot-survey-question-${question.feedbackQuestionID}`}
            minScore={question.minScore}
            maxScore={question.maxScore}
            minLabel={question.minLabel}
            maxLabel={question.maxLabel}
            value={scores[question.feedbackQuestionID]}
            onChange={handleScoreChange}
          />
        </div>
      ))}

      <div className="chatbot-survey-comments">
        <label htmlFor="chatbot-survey-comments" className="chatbot-survey-comments-label">
         {labels.feedbackInputTitle}
        </label>
        <textarea
          id="chatbot-survey-comments"
          className="chatbot-survey-comments-input"
          value={comments}
          onChange={(event) => {
            setComments(event.target.value);
            if (submitError) {
              setSubmitError('');
            }
          }}
          rows={3}
          maxLength={feedbackMaxLength}
          disabled={isSubmitting}
        />
      </div>

      {comments.length >= feedbackMaxLength && !submitError && (
        <p className="chatbot-survey-submit-error" role="alert">
          {maxCharsValidationMessage}
        </p>
      )}

      {submitError && (
        <p className="chatbot-survey-submit-error" role="alert" aria-live="assertive">
          {submitError}
        </p>
      )}
    </ModalComponent>
  );
};
